'use client';

/**
 * FlagIcon - Renders country/language flags as inline SVGs
 * ViewBox always 28x20, scales to any size without clipping
 */

interface FlagProps {
  code: string;
  width?: number;
  height?: number;
  className?: string;
}

export default function FlagIcon({ code, width = 28, height = 20, className = '' }: FlagProps) {
  const flag = getFlag(code.toLowerCase());
  
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 28 20"
      preserveAspectRatio="xMidYMid meet"
      className={`inline-block rounded-sm overflow-hidden flex-shrink-0 ${className}`}
      style={{ border: '1px solid rgba(0,0,0,0.08)' }}
    >
      {flag}
    </svg>
  );
}

function getFlag(code: string) {
  const w = 28, h = 20;
  const sh = h / 3;
  const sw = w / 3;

  switch (code) {
    // 🇪🇸 España
    case 'es':
      return (<>
        <rect width={w} height={h/4} fill="#AA151B" />
        <rect y={h/4} width={w} height={h/2} fill="#F1BF00" />
        <rect y={h*3/4} width={w} height={h/4} fill="#AA151B" />
      </>);

    // 🇺🇸 Estados Unidos
    case 'us':
      return (<>
        <rect width={w} height={h} fill="#B22234" />
        {[0,1,2,3,4,5].map(i => <rect key={i} y={h/13*(i*2+1)} width={w} height={h/13} fill="white" />)}
        <rect width={w*0.4} height={h*0.538} fill="#3C3B6E" />
        {[0,1,2].map(r => [0,1,2].map(c => <circle key={`${r}${c}`} cx={w*0.07+c*w*0.12} cy={h*0.07+r*h*0.15} r={1} fill="white" />))}
      </>);

    // 🇬🇧 UK / English
    case 'gb': case 'en':
      return (<>
        <rect width={w} height={h} fill="#012169" />
        <line x1={0} y1={0} x2={w} y2={h} stroke="white" strokeWidth={3} />
        <line x1={w} y1={0} x2={0} y2={h} stroke="white" strokeWidth={3} />
        <line x1={0} y1={0} x2={w} y2={h} stroke="#C8102E" strokeWidth={1.5} />
        <line x1={w} y1={0} x2={0} y2={h} stroke="#C8102E" strokeWidth={1.5} />
        <rect x={w/2-3} y={0} width={6} height={h} fill="white" />
        <rect x={0} y={h/2-3} width={w} height={6} fill="white" />
        <rect x={w/2-1.8} y={0} width={3.6} height={h} fill="#C8102E" />
        <rect x={0} y={h/2-1.8} width={w} height={3.6} fill="#C8102E" />
      </>);

    // 🇫🇷 Francia
    case 'fr':
      return (<>
        <rect width={sw} height={h} fill="#0055A4" />
        <rect x={sw} width={sw} height={h} fill="#FFFFFF" />
        <rect x={sw*2} width={sw} height={h} fill="#EF4135" />
      </>);

    // 🇩🇪 Alemania
    case 'de':
      return (<>
        <rect width={w} height={sh} fill="#000000" />
        <rect y={sh} width={w} height={sh} fill="#DD0000" />
        <rect y={sh*2} width={w} height={sh} fill="#FFCC00" />
      </>);

    // 🇮🇹 Italia
    case 'it':
      return (<>
        <rect width={sw} height={h} fill="#009246" />
        <rect x={sw} width={sw} height={h} fill="#FFFFFF" />
        <rect x={sw*2} width={sw} height={h} fill="#CE2B37" />
      </>);

    // 🇵🇹 Portugal
    case 'pt':
      return (<>
        <rect width={w*0.4} height={h} fill="#006600" />
        <rect x={w*0.4} width={w*0.6} height={h} fill="#FF0000" />
        <circle cx={w*0.4} cy={h/2} r={h*0.28} fill="#FFCC00" />
      </>);

    // 🇳🇱 Países Bajos
    case 'nl':
      return (<>
        <rect width={w} height={sh} fill="#AE1C28" />
        <rect y={sh} width={w} height={sh} fill="#FFFFFF" />
        <rect y={sh*2} width={w} height={sh} fill="#21468B" />
      </>);

    // 🇧🇪 Bélgica
    case 'be':
      return (<>
        <rect width={sw} height={h} fill="#000000" />
        <rect x={sw} width={sw} height={h} fill="#FDDA24" />
        <rect x={sw*2} width={sw} height={h} fill="#EF3340" />
      </>);

    // 🇨🇭 Suiza
    case 'ch':
      return (<>
        <rect width={w} height={h} fill="#FF0000" />
        <rect x={w*0.38} y={h*0.2} width={w*0.24} height={h*0.6} fill="white" />
        <rect x={w*0.2} y={h*0.38} width={w*0.6} height={h*0.24} fill="white" />
      </>);

    // 🇦🇹 Austria
    case 'at':
      return (<>
        <rect width={w} height={sh} fill="#ED2939" />
        <rect y={sh} width={w} height={sh} fill="#FFFFFF" />
        <rect y={sh*2} width={w} height={sh} fill="#ED2939" />
      </>);

    // 🇸🇪 Suecia
    case 'sv': case 'se':
      return (<>
        <rect width={w} height={h} fill="#006AA7" />
        <rect x={0} y={h*0.4} width={w} height={h*0.2} fill="#FECC00" />
        <rect x={w*0.3} y={0} width={h*0.2} height={h} fill="#FECC00" />
      </>);

    // 🇩🇰 Dinamarca
    case 'da': case 'dk':
      return (<>
        <rect width={w} height={h} fill="#C8102E" />
        <rect x={0} y={h*0.4} width={w} height={h*0.18} fill="#FFFFFF" />
        <rect x={w*0.3} y={0} width={h*0.18} height={h} fill="#FFFFFF" />
      </>);

    // 🇳🇴 Noruega
    case 'no':
      return (<>
        <rect width={w} height={h} fill="#BA0C2F" />
        <rect x={0} y={h*0.37} width={w} height={h*0.26} fill="white" />
        <rect x={w*0.27} y={0} width={h*0.26} height={h} fill="white" />
        <rect x={0} y={h*0.41} width={w} height={h*0.18} fill="#00205B" />
        <rect x={w*0.31} y={0} width={h*0.18} height={h} fill="#00205B" />
      </>);

    // 🇵🇱 Polonia
    case 'pl':
      return (<>
        <rect width={w} height={h/2} fill="#FFFFFF" />
        <rect y={h/2} width={w} height={h/2} fill="#DC143C" />
      </>);

    // 🇬🇷 Grecia
    case 'gr':
      return (<>
        <rect width={w} height={h} fill="#0D5EAF" />
        {[0,1,2,3].map(i => <rect key={i} y={h/9*(i*2+1)} width={w} height={h/9} fill="white" />)}
        <rect width={w*0.37} height={h*0.556} fill="#0D5EAF" />
        <rect x={0} y={h*0.222} width={w*0.37} height={h/9} fill="white" />
        <rect x={w*0.148} y={0} width={w*0.074} height={h*0.556} fill="white" />
      </>);

    // 🇹🇷 Turquía
    case 'tr':
      return (<>
        <rect width={w} height={h} fill="#E30A17" />
        <circle cx={w*0.36} cy={h/2} r={h*0.3} fill="white" />
        <circle cx={w*0.4} cy={h/2} r={h*0.24} fill="#E30A17" />
        <polygon points={`${w*0.52},${h*0.5} ${w*0.48},${h*0.37} ${w*0.56},${h*0.44} ${w*0.45},${h*0.44} ${w*0.53},${h*0.37}`} fill="white" />
      </>);

    // 🇲🇽 México
    case 'mx':
      return (<>
        <rect width={sw} height={h} fill="#006341" />
        <rect x={sw} width={sw} height={h} fill="#FFFFFF" />
        <rect x={sw*2} width={sw} height={h} fill="#CE1126" />
        <circle cx={w/2} cy={h/2} r={2.5} fill="#6D3A1F" />
      </>);

    // 🇦🇷 Argentina (codigo de PAIS).
    // OJO: 'ar' tambien es el codigo ISO 639 del idioma arabe. En este
    // proyecto FlagIcon se usa exclusivamente para idiomas, asi que
    // 'ar' se mapea abajo a la bandera arabe (Arabia Saudita). Si
    // alguna pantalla necesita la bandera de Argentina por pais,
    // debe pasar `code="arg"`.
    case 'arg':
      return (<>
        <rect width={w} height={sh} fill="#74ACDF" />
        <rect y={sh} width={w} height={sh} fill="#FFFFFF" />
        <rect y={sh*2} width={w} height={sh} fill="#74ACDF" />
        <circle cx={w/2} cy={h/2} r={2.5} fill="#F6B40E" stroke="#85340A" strokeWidth={0.3} />
      </>);

    // 🇧🇷 Brasil
    case 'br':
      return (<>
        <rect width={w} height={h} fill="#009739" />
        <polygon points={`${w/2},${h*0.12} ${w*0.88},${h/2} ${w/2},${h*0.88} ${w*0.12},${h/2}`} fill="#FEDD00" />
        <circle cx={w/2} cy={h/2} r={h*0.22} fill="#012169" />
      </>);

    // 🇨🇱 Chile
    case 'cl':
      return (<>
        <rect width={w*0.33} height={h/2} fill="#0033A0" />
        <rect x={w*0.33} width={w*0.67} height={h/2} fill="#FFFFFF" />
        <rect y={h/2} width={w} height={h/2} fill="#D52B1E" />
        <polygon points={`${w*0.165},${h*0.1} ${w*0.19},${h*0.2} ${w*0.28},${h*0.2} ${w*0.2},${h*0.27} ${w*0.22},${h*0.37} ${w*0.165},${h*0.3} ${w*0.11},${h*0.37} ${w*0.13},${h*0.27} ${w*0.05},${h*0.2} ${w*0.14},${h*0.2}`} fill="white" />
      </>);

    // 🇨🇴 Colombia
    case 'co':
      return (<>
        <rect width={w} height={h/2} fill="#FCD116" />
        <rect y={h/2} width={w} height={h/4} fill="#003893" />
        <rect y={h*3/4} width={w} height={h/4} fill="#CE1126" />
      </>);

    // 🇵🇪 Perú
    case 'pe':
      return (<>
        <rect width={sw} height={h} fill="#D91023" />
        <rect x={sw} width={sw} height={h} fill="#FFFFFF" />
        <rect x={sw*2} width={sw} height={h} fill="#D91023" />
      </>);

    // 🇯🇵 Japón
    case 'ja': case 'jp':
      return (<>
        <rect width={w} height={h} fill="#FFFFFF" />
        <circle cx={w/2} cy={h/2} r={h*0.3} fill="#BC002D" />
      </>);

    // 🇰🇷 Corea del Sur
    case 'ko': case 'kr':
      return (<>
        <rect width={w} height={h} fill="#FFFFFF" />
        <path d={`M ${w/2} ${h/2-h*0.25} A ${h*0.25} ${h*0.25} 0 0 1 ${w/2} ${h/2+h*0.25}`} fill="#CD2E3A" />
        <path d={`M ${w/2} ${h/2+h*0.25} A ${h*0.25} ${h*0.25} 0 0 1 ${w/2} ${h/2-h*0.25}`} fill="#0047A0" />
      </>);

    // 🇨🇳 China
    case 'zh': case 'cn':
      return (<>
        <rect width={w} height={h} fill="#DE2910" />
        <polygon points={`${w*0.15},${h*0.25} ${w*0.18},${h*0.35} ${w*0.28},${h*0.35} ${w*0.2},${h*0.42} ${w*0.23},${h*0.52} ${w*0.15},${h*0.45} ${w*0.07},${h*0.52} ${w*0.1},${h*0.42} ${w*0.02},${h*0.35} ${w*0.12},${h*0.35}`} fill="#FFDE00" />
      </>);

    // 🇮🇳 India
    case 'hi': case 'in':
      return (<>
        <rect width={w} height={sh} fill="#FF9933" />
        <rect y={sh} width={w} height={sh} fill="#FFFFFF" />
        <rect y={sh*2} width={w} height={sh} fill="#138808" />
        <circle cx={w/2} cy={h/2} r={sh*0.35} fill="none" stroke="#000080" strokeWidth={1} />
      </>);

    // 🇦🇺 Australia
    case 'au':
      return (<>
        <rect width={w} height={h} fill="#00008B" />
        <rect width={w*0.5} height={h*0.5} fill="#00008B" />
        <line x1={0} y1={0} x2={w*0.5} y2={h*0.5} stroke="white" strokeWidth={2} />
        <line x1={w*0.5} y1={0} x2={0} y2={h*0.5} stroke="white" strokeWidth={2} />
        <rect x={w*0.21} y={0} width={3.5} height={h*0.5} fill="white" />
        <rect x={0} y={h*0.21} width={w*0.5} height={3.5} fill="white" />
        <rect x={w*0.22} y={0} width={2} height={h*0.5} fill="#C8102E" />
        <rect x={0} y={h*0.22} width={w*0.5} height={2} fill="#C8102E" />
        <polygon points={`${w*0.7},${h*0.65} ${w*0.72},${h*0.72} ${w*0.78},${h*0.72} ${w*0.73},${h*0.77} ${w*0.75},${h*0.85} ${w*0.7},${h*0.8} ${w*0.65},${h*0.85} ${w*0.67},${h*0.77} ${w*0.62},${h*0.72} ${w*0.68},${h*0.72}`} fill="white" />
      </>);

    // 🇷🇺 Rusia
    case 'ru':
      return (<>
        <rect width={w} height={sh} fill="#FFFFFF" />
        <rect y={sh} width={w} height={sh} fill="#0039A6" />
        <rect y={sh*2} width={w} height={sh} fill="#D52B1E" />
      </>);

    // 🇺🇦 Ucrania
    case 'uk': case 'ua':
      return (<>
        <rect width={w} height={h/2} fill="#005BBB" />
        <rect y={h/2} width={w} height={h/2} fill="#FFD500" />
      </>);

    // 🇲🇦 Marruecos
    case 'ma':
      return (<>
        <rect width={w} height={h} fill="#C1272D" />
        <polygon points={`${w/2},${h*0.2} ${w*0.56},${h*0.42} ${w*0.7},${h*0.42} ${w*0.58},${h*0.56} ${w*0.63},${h*0.75} ${w/2},${h*0.63} ${w*0.37},${h*0.75} ${w*0.42},${h*0.56} ${w*0.3},${h*0.42} ${w*0.44},${h*0.42}`} fill="none" stroke="#006233" strokeWidth={1.2} />
      </>);

    // 🇸🇦 Arabia Saudí — usada tambien como bandera del idioma arabe ('ar').
    case 'ar': case 'sa':
      return (<>
        <rect width={w} height={h} fill="#006C35" />
        {/* "Shahada" estilizada: linea ondulada arriba */}
        <path
          d={`M ${w*0.15} ${h*0.36} Q ${w*0.25} ${h*0.30} ${w*0.35} ${h*0.36} T ${w*0.55} ${h*0.36} T ${w*0.75} ${h*0.36} T ${w*0.85} ${h*0.36}`}
          fill="none"
          stroke="white"
          strokeWidth={1.4}
          strokeLinecap="round"
        />
        {/* Espada horizontal blanca con punta a la izquierda */}
        <rect x={w*0.22} y={h*0.62} width={w*0.55} height={1.6} fill="white" />
        <polygon
          points={`${w*0.18},${h*0.69} ${w*0.22},${h*0.62} ${w*0.22},${h*0.755}`}
          fill="white"
        />
        {/* Mango */}
        <rect x={w*0.77} y={h*0.61} width={1.5} height={3} fill="white" />
      </>);

    // 🇦🇪 Emiratos Árabes
    case 'ae':
      return (<>
        <rect width={w*0.22} height={h} fill="#EF3340" />
        <rect x={w*0.22} width={w*0.78} height={sh} fill="#009739" />
        <rect x={w*0.22} y={sh} width={w*0.78} height={sh} fill="#FFFFFF" />
        <rect x={w*0.22} y={sh*2} width={w*0.78} height={sh} fill="#000000" />
      </>);

    // 🏴 Catalunya (Senyera)
    case 'ca': case 'cat': {
      const stripes = [];
      const senyeraH = h / 9;
      for (let i = 0; i < 9; i++) {
        stripes.push(
          <rect key={i} y={senyeraH * i} width={w} height={senyeraH} fill={i % 2 === 0 ? '#FCAC00' : '#DA121A'} />
        );
      }
      return <>{stripes}</>;
    }

    // 🏴 Galicia
    case 'gl': case 'gal':
      return (<>
        <rect width={w} height={h} fill="#FFFFFF" />
        <rect x={0} y={h/2-1} width={w} height={2} fill="#0073CF" />
        <rect x={w*0.35-1} y={0} width={2} height={h} fill="#0073CF" />
      </>);

    // 🏴 Euskadi (Ikurriña)
    case 'eu': case 'eus':
      return (<>
        <rect width={w} height={h} fill="#D52B1E" />
        <line x1={0} y1={0} x2={w} y2={h} stroke="#009B48" strokeWidth={3} />
        <line x1={w} y1={0} x2={0} y2={h} stroke="#009B48" strokeWidth={3} />
        <rect x={0} y={h/2-1.5} width={w} height={3} fill="white" />
        <rect x={w/2-1.5} y={0} width={3} height={h} fill="white" />
      </>);

    // Default - show country code text
    default:
      return (<>
        <rect width={w} height={h} fill="#E5E7EB" />
        <text x={w/2} y={h/2+1} textAnchor="middle" dominantBaseline="middle" fontSize={8} fill="#6B7280">{code.toUpperCase()}</text>
      </>);
  }
}
